/**
 * MyTeamPage — Portal del Colaborador.
 * Muestra los subordinados directos del empleado autenticado con su info
 * básica y vacaciones pendientes. Permite a un jefe ver a su equipo.
 *
 * La foto de cada persona la ve toda la organización (plan del portal, D4).
 */
import { useState } from 'react'
import { Users } from 'lucide-react'
import { Avatar } from '@/components/ds/Avatar'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/ds/EmptyState'
import { usePortalMyTeam } from '../hooks/useEmployeePortal'
import { PaginaPortal } from '../components/PaginaPortal'
import { dias, fechaCorta } from '../components/formatos'
import { SolicitudesPorResolver } from '../components/SolicitudesPorResolver'
import { FichaDelIntegrante } from '../components/FichaDelIntegrante'

export default function MyTeamPage() {
  const { data, isLoading, isError } = usePortalMyTeam()
  // La ficha se abre en un panel lateral: la lista sigue a la vista.
  const [fichaDe, setFichaDe] = useState<string | null>(null)
  const team = data?.team ?? []

  const bajada = isLoading
    ? 'Personas que reportan directamente.'
    : team.length === 0
      ? 'Sin personas a cargo por ahora.'
      : `${team.length} ${team.length === 1 ? 'persona reporta' : 'personas reportan'} directamente.`

  return (
    <PaginaPortal titulo="Mi equipo" bajada={bajada}>
      <div className="flex flex-col gap-5">
      <SolicitudesPorResolver />
      {isError ? (
        <EmptyState variant="error" title="No se pudo cargar el equipo" />
      ) : (
        <DataTable
          rows={team}
          rowKey={m => m.id}
          onRowClick={m => setFichaDe(m.id)}
          loading={isLoading}
          minWidth={820}
          caption="Personas que reportan directamente"
          empty={
            <EmptyState
              icon={<Users size={28} aria-hidden="true" />}
              title="Sin personas a cargo"
              description="Cuando alguien tenga esta jefatura asignada, va a aparecer acá."
            />
          }
          columns={[
            {
              key: 'name', header: 'Persona', sortable: true,
              render: m => (
                <div className="flex items-center gap-3">
                  <Avatar nombre={m.name} fotoUrl={m.photo_url} tamano="sm" decorativa />
                  <div>
                    <div className="font-medium text-content">{m.name}</div>
                    <div className="text-xs text-content-subtle font-mono">{m.rut}</div>
                  </div>
                </div>
              ),
            },
            {
              key: 'position', header: 'Cargo', sortable: true,
              render: m => <span className="text-content">{m.position || '—'}</span>,
            },
            {
              key: 'department', header: 'Área', sortable: true,
              render: m => <span className="text-content">{m.department || '—'}</span>,
            },
            {
              key: 'hire_date', header: 'Ingreso', sortable: true, width: 120,
              render: m => <span className="text-content-muted tabular-nums">{fechaCorta(m.hire_date)}</span>,
            },
            {
              key: 'vacation_pending_days', header: 'Vacaciones', numeric: true, sortable: true,
              // ⚠️ Quien no tiene contrato vigente no tiene saldo, y ordenarlo
              // como 0 lo pondria junto a quien agoto sus dias: son cosas
              // distintas, asi que va como vacio y el componente lo manda al final.
              sortValue: m => (m.has_current_contract ? m.vacation_pending_days : null),
              render: m => m.has_current_contract
                ? <span className="font-medium text-content tabular-nums">{dias(m.vacation_pending_days)} días</span>
                : <span className="text-xs text-warning-content">sin contrato</span>,
            },
          ]}
        />
      )}
      </div>
      <FichaDelIntegrante id={fichaDe} onClose={() => setFichaDe(null)} />
    </PaginaPortal>
  )
}
